import { forwardRef, useCallback, useRef, useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';
import type { DropZoneProps, DropZoneVariant, DropZoneSize } from './DropZone.types';
import { Button } from '../Button';
import { Message } from '../Message';

/**
 * Get size-based styles
 */
const getSizeStyles = (size: DropZoneSize, theme: any) => {
  switch (size) {
    case 'sm':
      return {
        padding: '16px',
        fontSize: theme.typography.fontSize.xs,
        iconSize: '24px',
        minHeight: '80px',
      };
    case 'lg':
      return {
        padding: '32px',
        fontSize: theme.typography.fontSize.md,
        iconSize: '48px',
        minHeight: '180px',
      };
    case 'md':
    default:
      return {
        padding: '24px',
        fontSize: theme.typography.fontSize.sm,
        iconSize: '36px',
        minHeight: '120px',
      };
  }
};

/**
 * Root container for the drop zone with error message support
 */
const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

/**
 * Container for the drop zone
 */
const DropZoneContainer = styled.div<{
  $isDragOver: boolean;
  $disabled: boolean;
  $variant: DropZoneVariant;
  $size: DropZoneSize;
  $error: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: ${({ theme }) => theme.transitions.fast};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  box-sizing: border-box;
  
  /* Size styles */
  ${({ $size, theme }) => {
    const sizeStyles = getSizeStyles($size, theme);
    return `
      padding: ${sizeStyles.padding};
      font-size: ${sizeStyles.fontSize};
      min-height: ${sizeStyles.minHeight};
    `;
  }}
  
  /* Variant styles */
  ${({ $variant, $isDragOver, $disabled, $error, theme }) => {
    const borderColor = $error
      ? theme.colors.status.error
      : ($isDragOver && !$disabled ? theme.colors.highlight : theme.colors.border.medium);
    
    if ($variant === 'compact') {
      return `
        border: ${theme.borders.width.thin} dashed ${borderColor};
        border-radius: ${theme.borders.radius.sm};
        background-color: ${$isDragOver && !$disabled ? `color-mix(in srgb, ${theme.colors.highlight} 8%, transparent)` : theme.colors.background.default};
      `;
    }
    
    return `
      border: ${theme.borders.width.medium} dashed ${borderColor};
      border-radius: ${theme.borders.radius.md};
      background-color: ${$isDragOver && !$disabled ? `color-mix(in srgb, ${theme.colors.highlight} 8%, transparent)` : theme.colors.background.hover};
    `;
  }}
  
  /* Hover state */
  &:hover:not([data-disabled="true"]) {
    border-color: ${({ theme, $isDragOver, $error }) =>
      $error ? theme.colors.status.error : ($isDragOver ? theme.colors.highlight : theme.colors.text.secondary)};
    background-color: ${({ theme }) => theme.colors.background.active};
  }
  
  /* Disabled state */
  ${({ $disabled, theme }) => $disabled && `
    opacity: 0.5;
    background-color: ${theme.colors.background.disabled};
    pointer-events: none;
  `}
  
  /* Focus visible for accessibility */
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.highlight};
    outline-offset: 2px;
  }
`;

/**
 * Hidden file input
 */
const HiddenInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

/**
 * Default content container
 */
const DefaultContent = styled.div<{ $size: DropZoneSize }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/**
 * Upload icon SVG
 */
const UploadIcon = styled.svg<{ $size: DropZoneSize; $error?: boolean }>`
  ${({ $size, theme }) => {
    const sizeStyles = getSizeStyles($size, theme);
    return `
      width: ${sizeStyles.iconSize};
      height: ${sizeStyles.iconSize};
    `;
  }}
  fill: ${({ theme, $error }) => $error ? theme.colors.status.error : 'currentColor'};
`;

/**
 * Text content
 */
const DropText = styled.span<{ $error?: boolean }>`
  color: ${({ theme, $error }) => $error ? theme.colors.status.error : theme.colors.text.secondary};
`;

/**
 * Browse link styled text
 */
const BrowseText = styled.span<{ $error?: boolean }>`
  color: ${({ theme, $error }) => $error ? theme.colors.status.error : theme.colors.highlight};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  text-decoration: underline;
  
  &:hover {
    text-decoration: underline;
  }
`;

/**
 * Hint text for file size or types
 */
const HintText = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
`;

/**
 * Spacer for visual separation between hint sections
 */
const HintSpacer = styled.div`
  height: 4px;
`;

/**
 * Preview wrapper container
 */
const PreviewWrapper = styled.div`
  position: relative;
  width: 100%;
`;

/**
 * Preview container with styling
 */
const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.colors.border.medium};
  border-radius: ${({ theme }) => theme.borders.radius.sm};
  padding: 24px;
  position: relative;
  box-sizing: border-box;
`;

/**
 * Preview image container
 */
const PreviewImageContainer = styled.div`
  width: 240px;
  height: 160px;
  border-radius: ${({ theme }) => theme.borders.radius.sm};
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.colors.border.medium};
`;

/**
 * Preview image
 */
const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

/**
 * Preview placeholder for non-image files
 */
const PreviewPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.background.hover};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

/**
 * Preview file info container
 */
const PreviewInfo = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
`;

/**
 * File name and size text
 */
const FileInfoText = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

/**
 * Progress bar animation
 */
const progressAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 100% 50%;
  }
`;

/**
 * Progress bar container
 */
const ProgressBar = styled.div<{ $progress: number }>`
  width: 100%;
  height: 4px;
  background-color: ${({ theme }) => theme.colors.border.light};
  border-radius: 2px;
  overflow: hidden;
  margin-top: 8px;
`;

/**
 * Progress bar fill
 */
const ProgressFill = styled.div<{ $progress: number; $uploading?: boolean }>`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background-color: ${({ theme }) => theme.colors.highlight};
  border-radius: 2px;
  transition: width 0.3s ease;
  
  ${({ $uploading }) => $uploading && css`
    background: linear-gradient(
      90deg,
      var(--color-highlight, #0066cc) 0%,
      var(--color-highlight-light, #3399ff) 50%,
      var(--color-highlight, #0066cc) 100%
    );
    background-size: 200% 100%;
    animation: ${progressAnimation} 1.5s linear infinite;
  `}
`;

/**
 * Progress text
 */
const ProgressText = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  margin-top: 4px;
`;

/**
 * Clear/Remove icon button
 */
const ClearIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

/**
 * Format bytes to human readable string
 */
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

/**
 * Check if a file is an image
 */
const isImageFile = (file: File): boolean => {
  return file.type.startsWith('image/');
};

/**
 * Check if a URL points to an image
 */
const isImageUrl = (url: string): boolean => {
  if (url.includes('unsplash.com') || url.includes('images.unsplash.com')) {
    return true;
  }
  if (url.startsWith('blob:')) {
    return true;
  }
  if (url.startsWith('data:image/')) {
    return true;
  }
  const urlWithoutQuery = url.split('?')[0]?.split('#')[0] || url;
  return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(urlWithoutQuery);
};

/**
 * DropZone component - A drag-and-drop file input area
 *
 * @example
 * ```tsx
 * const [files, setFiles] = useState<File[]>([]);
 *
 * <DropZone
 *   onFilesSelect={(files) => setFiles(files)}
 *   accept={['image/*', '.pdf']}
 *   multiple
 * />
 *
 * // With preview mode
 * <DropZone
 *   onFilesSelect={handleFiles}
 *   showPreview
 *   maxSize={5 * 1024 * 1024}
 *   onError={handleError}
 * />
 *
 * // With progress indicator
 * <DropZone
 *   onFilesSelect={handleFiles}
 *   showPreview
 *   progress={uploadProgress}
 *   uploading={isUploading}
 * />
 *
 * // With existing file
 * <DropZone
 *   onFilesSelect={handleFiles}
 *   showPreview
 *   existingFile={{ url: 'https://...', name: 'image.jpg' }}
 *   onClearExisting={() => setExistingFile(null)}
 * />
 * ```
 */
export const DropZone = forwardRef<HTMLDivElement, DropZoneProps>(
  (
    {
      onFilesSelect,
      accept,
      multiple = false,
      disabled = false,
      variant = 'default',
      size = 'md',
      children,
      showDefaultContent = true,
      maxSizeHint,
      className,
      'aria-label': ariaLabel = 'Drop files here or click to browse',
      'data-testid': testId,
      dataTestId,
      dataId,
      maxSize,
      onError,
      text,
      browseText,
      showPreview = false,
      changeButtonText,
      onChangeClick,
      value,
      existingFile,
      onClearExisting,
      allowRemove = true,
      error,
      setError,
      progress,
      uploading,
      onClear,
      sizeHintText,
    },
    ref
  ) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [previewUrls, setPreviewUrls] = useState<string[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);
    const dragCounter = useRef(0);
    const isDialogOpenRef = useRef(false);

    const acceptString = accept?.join(',');

    // Update selected files when value prop changes (controlled mode)
    useEffect(() => {
      if (value) {
        setSelectedFiles(value);
      }
    }, [value]);

    // Generate preview URLs for image files
    useEffect(() => {
      const urls: string[] = [];
      selectedFiles.forEach(file => {
        if (isImageFile(file)) {
          urls.push(URL.createObjectURL(file));
        } else {
          urls.push('');
        }
      });
      setPreviewUrls(urls);

      return () => {
        urls.forEach(url => {
          if (url) URL.revokeObjectURL(url);
        });
      };
    }, [selectedFiles]);

    /**
     * Validate files against maxSize constraint
     */
    const validateFiles = useCallback(
      (files: FileList | File[]): boolean => {
        if (maxSize) {
          const fileArray = Array.isArray(files) ? files : Array.from(files);
          for (const file of fileArray) {
            if (file.size > maxSize) {
              const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(2);
              const errorMessage = `File size exceeds maximum allowed size of ${maxSizeMB} MB`;
              onError?.(errorMessage);
              setError?.(errorMessage);
              return false;
            }
          }
        }
        return true;
      },
      [maxSize, onError, setError]
    );

    /**
     * Handle file selection
     */
    const handleFileSelect = useCallback(
      (files: FileList | File[] | null) => {
        if (!files || (Array.isArray(files) ? files.length === 0 : files.length === 0) || disabled) return;

        const fileArray = Array.isArray(files) ? files : Array.from(files);

        if (validateFiles(fileArray)) {
          const filesToSelect = multiple ? fileArray : fileArray.slice(0, 1);

          if (showPreview) {
            setSelectedFiles(filesToSelect);
          }

          onFilesSelect(filesToSelect);
        }
      },
      [disabled, multiple, onFilesSelect, validateFiles, showPreview]
    );

    const handleDragEnter = useCallback(
      (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled) return;
        
        dragCounter.current++;
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
          setIsDragOver(true);
        }
      },
      [disabled]
    );

    const handleDragLeave = useCallback(
      (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled) return;
        
        dragCounter.current--;
        if (dragCounter.current === 0) {
          setIsDragOver(false);
        }
      },
      [disabled]
    );

    const handleDragOver = useCallback(
      (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
      },
      []
    );

    const handleDrop = useCallback(
      (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled) return;
        
        setIsDragOver(false);
        dragCounter.current = 0;

        const droppedFiles = e.dataTransfer.files;
        if (droppedFiles && droppedFiles.length > 0) {
          handleFileSelect(droppedFiles);
        }
      },
      [disabled, handleFileSelect]
    );

    const handleClick = useCallback(() => {
      if (disabled || isDialogOpenRef.current) return;
      isDialogOpenRef.current = true;
      inputRef.current?.click();
    }, [disabled]);

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        isDialogOpenRef.current = false;
        const selectedInputFiles = e.target.files;
        if (selectedInputFiles && selectedInputFiles.length > 0) {
          handleFileSelect(selectedInputFiles);
        }
        e.target.value = '';
      },
      [handleFileSelect]
    );

    const handleInputFocus = useCallback(() => {
      isDialogOpenRef.current = true;
    }, []);

    const handleInputBlur = useCallback(() => {
      setTimeout(() => {
        if (isDialogOpenRef.current) {
          isDialogOpenRef.current = false;
        }
      }, 100);
    }, []);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          inputRef.current?.click();
        }
      },
      [disabled]
    );

    const handleChangeClick = useCallback(() => {
      if (onChangeClick) {
        onChangeClick();
      } else {
        if (!disabled && inputRef.current && !isDialogOpenRef.current) {
          isDialogOpenRef.current = true;
          inputRef.current.click();
        }
      }
    }, [onChangeClick, disabled]);

    const handleClearAll = useCallback(() => {
      setSelectedFiles([]);
      setPreviewUrls([]);
      
      if (inputRef.current) {
        inputRef.current.value = '';
      }

      onClear?.();
      
      // Notify parent with empty array
      onFilesSelect([]);
    }, [onFilesSelect, onClear]);

    const handleClearExisting = useCallback(() => {
      if (onClearExisting) {
        onClearExisting();
      }
    }, [onClearExisting]);

    // Display text
    const displayText = text || `Drag & drop ${multiple ? 'files' : 'a file'} here, or`;
    const browseDisplayText = browseText || 'browse';
    const changeDisplayText = changeButtonText || 'Change';

    // Get accessibility label
    const getAccessibilityLabel = () => {
      const textStr = typeof displayText === 'string' 
        ? displayText 
        : 'Drag and drop a file to upload or click';
      const browseStr = typeof browseDisplayText === 'string' ? browseDisplayText : 'Browse';
      return `File upload. ${textStr} ${browseStr}`;
    };

    // Check if we should show preview
    const shouldShowPreview = showPreview && (selectedFiles.length > 0 || existingFile);

    /**
     * Render preview for existing file from URL
     */
    const renderExistingFilePreview = () => {
      if (!existingFile) return null;

      const isImage = isImageUrl(existingFile.url);

      return (
        <PreviewContainer key="existing-file">
          <PreviewImageContainer>
            {isImage ? (
              <PreviewImage
                src={existingFile.url}
                alt={existingFile.name || 'Existing file'}
              />
            ) : (
              <PreviewPlaceholder>
                No preview available
              </PreviewPlaceholder>
            )}
          </PreviewImageContainer>

          <PreviewInfo>
            <FileInfoText>
              {existingFile.name || 'Existing file'}
              {existingFile.size ? ` (${formatBytes(existingFile.size)})` : ''}
            </FileInfoText>
            {allowRemove && (
              <Button
                icon={<ClearIcon />}
                aria-label="Clear existing file"
                onClick={handleClearExisting}
                disabled={disabled}
                variant="ghost"
                size="sm"
              />
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={handleChangeClick}
              disabled={disabled}
            >
              {changeDisplayText}
            </Button>
          </PreviewInfo>

          {/* Progress bar for existing file upload */}
          {typeof progress === 'number' && (
            <>
              <ProgressBar $progress={progress}>
                <ProgressFill $progress={progress} $uploading={uploading} />
              </ProgressBar>
              <ProgressText>{progress}% uploaded</ProgressText>
            </>
          )}
        </PreviewContainer>
      );
    };

    /**
     * Render preview for a single uploaded file
     */
    const renderFilePreview = (file: File, index: number) => {
      const previewUrl = previewUrls[index];

      return (
        <PreviewContainer key={`${file.name}-${index}`}>
          <PreviewImageContainer>
            {isImageFile(file) && previewUrl ? (
              <PreviewImage src={previewUrl} alt={file.name} />
            ) : (
              <PreviewPlaceholder>
                No preview available
              </PreviewPlaceholder>
            )}
          </PreviewImageContainer>

          <PreviewInfo>
            <FileInfoText>
              {file.name} ({formatBytes(file.size)})
            </FileInfoText>
            {allowRemove && (
              <Button
                icon={<ClearIcon />}
                aria-label="Clear all"
                onClick={handleClearAll}
                disabled={disabled || uploading}
                variant="ghost"
                size="sm"
              />
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={handleChangeClick}
              disabled={disabled || uploading}
            >
              {changeDisplayText}
            </Button>
          </PreviewInfo>

          {/* Progress bar */}
          {typeof progress === 'number' && (
            <>
              <ProgressBar $progress={progress}>
                <ProgressFill $progress={progress} $uploading={uploading} />
              </ProgressBar>
              <ProgressText>{progress}% uploaded</ProgressText>
            </>
          )}
        </PreviewContainer>
      );
    };

    // If showing preview and we have files or existing file, render preview mode
    if (shouldShowPreview && !error) {
      return (
        <PreviewWrapper ref={ref} className={className} data-testid={dataTestId || testId} data-id={dataId}>
          <HiddenInput
            ref={inputRef}
            type="file"
            accept={acceptString}
            multiple={multiple}
            disabled={disabled}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            tabIndex={-1}
            aria-hidden="true"
            data-testid={testId ? `${testId}-input` : undefined}
            aria-label={getAccessibilityLabel()}
            title={getAccessibilityLabel()}
          />

          {/* Show existing file if present and no new files selected */}
          {existingFile && selectedFiles.length === 0 && renderExistingFilePreview()}

          {/* Show newly selected files */}
          {selectedFiles.map((file, index) => renderFilePreview(file, index))}
        </PreviewWrapper>
      );
    }

    // Default upload mode
    return (
      <Root>
        <DropZoneContainer
          ref={ref}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={ariaLabel}
          aria-disabled={disabled}
          data-disabled={disabled}
          data-testid={dataTestId || testId}
          data-id={dataId}
          className={className}
          $isDragOver={isDragOver}
          $disabled={disabled}
          $variant={variant}
          $size={size}
          $error={!!error}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <HiddenInput
            ref={inputRef}
            type="file"
            accept={acceptString}
            multiple={multiple}
            disabled={disabled}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            tabIndex={-1}
            aria-hidden="true"
            data-testid={testId ? `${testId}-input` : undefined}
          />
          
          {children ? (
            children
          ) : showDefaultContent ? (
            <DefaultContent $size={size}>
              <UploadIcon
                $size={size}
                $error={!!error}
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z" />
              </UploadIcon>
              <DropText $error={!!error}>
                {displayText}{' '}
                <BrowseText $error={!!error}>{browseDisplayText}</BrowseText>
              </DropText>
              {(sizeHintText || maxSizeHint || maxSize || accept) && (
                <>
                  {accept && (
                    <HintText>
                      {`Formats: ${accept.join(', ')}`}
                    </HintText>
                  )}
                  {accept && (sizeHintText || maxSizeHint || maxSize) && (
                    <HintSpacer />
                  )}
                  {(sizeHintText || maxSizeHint || maxSize) && (
                    <HintText>
                      {sizeHintText || `Max size: ${formatBytes(maxSizeHint || maxSize || 0)}`}
                    </HintText>
                  )}
                </>
              )}
            </DefaultContent>
          ) : null}
        </DropZoneContainer>
        {error && (
          <Message variant="error" size="sm" dataTestId={testId ? `${testId}-error` : undefined}>
            {error}
          </Message>
        )}
      </Root>
    );
  }
);

DropZone.displayName = 'DropZone';

export default DropZone;
